import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, FilterOptionsState } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import { deburr } from 'lodash';
import { getOwner } from 'api';
import { IOwnerModel, IOwnerRoomModel, IRoomModel } from 'interfaces';
import useError from 'components/ErrorProvider/useError';
import { getRooms } from '../api';

interface IProps {
  realEstateGuid: string;
  clientRooms: IOwnerRoomModel[];
  room: IRoomModel | null;
  setRoom: (room: IRoomModel | null) => void;
  setRoomOwner: (owner: IOwnerModel | null) => void;
  disabled: boolean;
  isSyncing: boolean;
}

const RoomField: React.FC<IProps> = ({
  realEstateGuid,
  clientRooms,
  disabled,
  room,
  isSyncing,
  setRoom,
  setRoomOwner,
}) => {
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState<IRoomModel[]>([]);

  const { addError } = useError();

  useEffect(() => {
    if (!realEstateGuid) return setRooms([]);
    setLoading(true);

    getRooms(realEstateGuid)
      .then(setRooms)
      .catch((error) => {
        addError(error.response.data.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isSyncing, realEstateGuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const roomOwnerEffect = useCallback(() => {
    if (!room) return setRoomOwner(null);
    setLoading(true);

    getOwner(room.guid)
      .then(setRoomOwner)
      .catch((error) => {
        addError(error.response.data.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [room, setRoomOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(roomOwnerEffect, [room]);

  const clientRoomsIds = [...clientRooms.map((clientRoom: IOwnerRoomModel) => clientRoom.roomGuid)],
    options = [
      ...clientRooms
        .filter((item) => realEstateGuid && item.realEstateGuid === realEstateGuid)
        .map((item) => ({
          ...item,
          guid: item.roomGuid,
          name: item.roomName,
          contract: item.contractGuid,
          groupName: 'Помещения клиента',
        })),
      ...rooms.filter((item) => !clientRoomsIds.includes(item.guid)).map((item) => ({ ...item, groupName: 'Другие' })),
    ] as (IRoomModel & { groupName: string })[];

  const renderOption = (item: IRoomModel): React.ReactNode => {
    return (
      <div>
        <p style={{ margin: 0 }}>
          {item.name} ({item.propertyType})
        </p>
        {item.kontragentName && item.kontragentName.length > 0 && (
          <p style={{ margin: 0, color: ' rgba(0, 0, 0, 0.5)' }}>
            <small>{item.kontragentName}</small>
          </p>
        )}
      </div>
    );
  };

  const filterOptions = (options: IRoomModel[], { inputValue }: FilterOptionsState<any>): IRoomModel[] => {
    const searchValue: string = inputValue === null ? '' : deburr(inputValue.trim()).toLowerCase();

    return options.filter(
      (option) =>
        option.name.toLowerCase().indexOf(searchValue) > -1 ||
        (option.kontragentName && option.kontragentName.toLowerCase().indexOf(searchValue) > -1),
    );
  };

  return (
    <Autocomplete
      value={room}
      groupBy={(option: IRoomModel & { groupName: string }) => option.groupName}
      getOptionLabel={(option: IRoomModel) => option.name}
      loading={loading}
      disabled={loading || !realEstateGuid || disabled}
      onChange={(event, option) => setRoom(option)}
      options={options}
      renderInput={(props) => <TextField {...props} variant="filled" label="Помещение" />}
      renderOption={renderOption}
      filterOptions={filterOptions}
    />
  );
};

export default RoomField;
